import type { BaseQueryFn } from '@reduxjs/toolkit/query';
import { fetchBaseQuery } from '@reduxjs/toolkit/query';
import { stringify } from 'qs';

import { ENVS, HTTPStatusCode } from '@/shared/consts';
import { getCasdoorAccessToken, getCasdoorRefreshToken, logoutUser, saveCasdoorTokens } from '@/shared/helpers';

import { EXCLUDED_AUTHORIZATION_TOKEN_ENDPOINTS } from '../consts';
import type { ICasdoorAuthDataResponse } from '../types';

import { casdoorMutex } from './casdoorMutex';

export const publicBaseQuery = ({ baseUrl }: { baseUrl: string }) =>
  fetchBaseQuery({
    baseUrl,
    paramsSerializer: (params) => {
      if (params instanceof URLSearchParams) {
        return params.toString();
      }

      return stringify(params, {
        arrayFormat: 'brackets',
        skipNulls: true,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        filter: (_, value) => value || undefined,
      });
    },
    prepareHeaders: (headers, { endpoint }) => {
      headers.set('Content-Type', 'application/json');
      headers.set('Accept-Language', 'ru');

      const accessToken = getCasdoorAccessToken();
      const isExcludedEndpoint = EXCLUDED_AUTHORIZATION_TOKEN_ENDPOINTS.includes(endpoint);

      if (accessToken && !isExcludedEndpoint) {
        headers.set('Authorization', `Bearer ${accessToken}`);
      }

      return headers;
    },
  });

export const publicBaseQueryWithReauth =
  ({ baseUrl }: { baseUrl: string }): BaseQueryFn =>
  async (args, api, extraOptions) => {
    const baseQueryWithBaseUrl = publicBaseQuery({ baseUrl });

    await casdoorMutex.waitForUnlock();
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    let result = await baseQueryWithBaseUrl(args, api, extraOptions);

    if (result.error?.status === HTTPStatusCode.UnAuthorized) {
      if (!casdoorMutex.isLocked()) {
        const refreshToken = getCasdoorRefreshToken();

        if (!refreshToken) {
          logoutUser();

          return result;
        }

        const release = await casdoorMutex.acquire();

        try {
          const { data } = await baseQueryWithBaseUrl(
            {
              method: 'POST',
              url: `${ENVS.CASDOOR_API_URL}/login/oauth/refresh_token`,
              params: {
                grant_type: 'refresh_token',
                refresh_token: refreshToken,
                scope: 'openid+profile+email',
                client_id: ENVS.CASDOOR_CLIENT_ID,
              },
            },
            { ...api, endpoint: 'refresh_token' },
            extraOptions,
          );

          if (data) {
            const { access_token: casdoorAccessToken, refresh_token: casdoorRefreshToken } =
              data as ICasdoorAuthDataResponse;

            saveCasdoorTokens({ casdoorAccessToken, casdoorRefreshToken });
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            result = await baseQueryWithBaseUrl(args, api, extraOptions);
          } else {
            logoutUser();
          }
        } catch {
          logoutUser();
        } finally {
          release();
        }
      } else {
        await casdoorMutex.waitForUnlock();
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        result = await baseQueryWithBaseUrl(args, api, extraOptions);
      }
    }

    return result;
  };
